import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import localePtPt from '@angular/common/locales/pt-PT';
import { LOCALE_ID, NgModule, isDevMode } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  AlertModule,
  CLIENT_SURFACE,
  CONSTANTS,
  ClientSurfaceEnum,
  ENV,
  JwtService,
  LanguageHeaderInterceptor,
  LanguageService,
  MultiTranslateHttpLoader,
  TrendMissingTranslationHandler,
} from 'lib-trend-core';
import {
  CHAT_CONTEXT_REF_NAVIGATOR,
  CHAT_PUSH_CONFIG,
  ChatPreferencesModule,
  ChatPushConfig,
  ChatSharedModule,
  ChatStateModule,
  ChatStatusModule,
  ChatUiModule,
} from 'lib-trend-core/chat';
import { NgxCaptchaModule } from 'ngx-captcha';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { CHAT_APP_PUSH_WORKER_SCOPE, CHAT_APP_PUSH_WORKER_URL, ChatAppConstants } from './chat-app.constants';
import { ChatAppCommonModule } from './chat-app-common.module';
import { ChatAppRoutingModule } from './chat-app-routing.module';
import { ChatAppComponent } from './chat-app.component';
import { ChatAppApiUrlInterceptor } from './core/chat-app-api-url.interceptor';
import { ChatAppContextRefNavigator } from './core/chat-app-context-ref-navigator';
import { ChatAppHttpErrorInterceptor } from './core/chat-app-http-error.interceptor';
import { ChatAppHandoffComponent } from './pages/handoff/chat-app-handoff.component';
import { ChatAppSignInComponent } from './pages/sign-in/chat-app-sign-in.component';
import { ChatAppUnavailableComponent } from './pages/unavailable/chat-app-unavailable.component';
import { ChatAppConversationRouteComponent } from './routes/chat-app-conversation-route.component';
import { ChatAppDirectRouteComponent } from './routes/chat-app-direct-route.component';
import { ChatAppHomeComponent } from './routes/chat-app-home.component';
import { ChatAppInstallStepsComponent } from './shell/chat-app-install-steps.component';
import { ChatAppRailResizeDirective } from './shell/chat-app-rail-resize.directive';
import { ChatAppShellComponent } from './shell/chat-app-shell.component';

registerLocaleData(localeEn, 'en');
registerLocaleData(localePt, 'pt-BR');
registerLocaleData(localePtPt, 'pt-PT');
registerLocaleData(localeEs, 'es');

/** App catalog (`chatApp.*`) + the two library catalogs, loaded outside the API interceptors. */
export function chatAppTranslateLoaderFactory(handler: HttpBackend): TranslateLoader {
  return new MultiTranslateHttpLoader(new HttpClient(handler), [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/i18n/lib-core.', suffix: '.json' },
    { prefix: './assets/i18n/lib-chat.', suffix: '.json' },
  ]);
}

/** Web push of the chat app: its own messaging worker, off the Angular worker's scope. */
const chatPushConfig: ChatPushConfig = {
  apiUrl: environment.apiUrl,
  firebase: environment.firebase,
  serviceWorkerUrl: CHAT_APP_PUSH_WORKER_URL,
  serviceWorkerScope: CHAT_APP_PUSH_WORKER_SCOPE,
  prePrompt: false,
};

/**
 * Trendmulti Chat — the standalone, installable internal chat
 * (SPEC-2026-09-internal-chat-pwa, ADR-001 · T-30).
 */
@NgModule({ declarations: [
        ChatAppComponent,
        ChatAppShellComponent,
        ChatAppRailResizeDirective,
        ChatAppHomeComponent,
        ChatAppConversationRouteComponent,
        ChatAppDirectRouteComponent,
        ChatAppSignInComponent,
        ChatAppHandoffComponent,
        ChatAppUnavailableComponent,
        ChatAppInstallStepsComponent,
    ],
    bootstrap: [ChatAppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        TranslateModule.forRoot({
            defaultLanguage: 'pt-BR',
            useDefaultLang: true,
            loader: {
                provide: TranslateLoader,
                useFactory: chatAppTranslateLoaderFactory,
                deps: [HttpBackend],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: TrendMissingTranslationHandler,
            },
        }),
        StoreModule.forRoot(),
        EffectsModule.forRoot(),
        AlertModule,
        ToastrModule.forRoot({ preventDuplicates: true, maxOpened: 3 }),
        MatNativeDateModule,
        NgxCaptchaModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            // The Angular worker is emitted by the staging and production builds only.
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
        ChatUiModule,
        ChatStateModule,
        ChatSharedModule,
        ChatStatusModule,
        ChatPreferencesModule,
        ChatAppCommonModule,
        ChatAppRoutingModule], providers: [
        { provide: ENV, useValue: environment },
        { provide: CONSTANTS, useValue: ChatAppConstants },
        { provide: CLIENT_SURFACE, useValue: ClientSurfaceEnum.WEB_CHAT },
        { provide: CHAT_PUSH_CONFIG, useValue: chatPushConfig },
        { provide: CHAT_CONTEXT_REF_NAVIGATOR, useClass: ChatAppContextRefNavigator },
        { provide: HTTP_INTERCEPTORS, useClass: ChatAppApiUrlInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: JwtService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LanguageHeaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ChatAppHttpErrorInterceptor, multi: true },
        { provide: LOCALE_ID, useFactory: (language: LanguageService) => language.currentLang, deps: [LanguageService] },
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        // CDK 21 renders overlays in the browser top layer by default; keeping them in the overlay
        // container lets SweetAlert2 and toasts still stack above Material dialogs.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ] })
export class ChatAppModule { }
